export const CANONICAL_THREAD_TITLE_MAX_LENGTH = 200;

/**
 * What a person may type into a name field — the rename inputs' long-standing
 * cap (the rail row, the header, and since 2026-09-13 the new-thread popup),
 * which is why it lives here beside the rest of "what a thread title may be"
 * rather than as a `42` repeated in four components.
 */
export const THREAD_NAME_MAX_LEN = 42;

const RESERVED_THREAD_TITLES = new Set([
  'group thread',
  'new thread',
  'nuevo hilo',
]);

/**
 * A stored title that is really a PLACEHOLDER, not a name anyone chose.
 *
 * These strings are written by the platform, not by people: the client mints
 * "New thread" / "Nuevo hilo" locally, and the server stubs a newly-surfaced
 * participant's index doc with "Group thread". Until 2026-09-13 the two sides
 * disagreed about which one a given person saw, so the creator read "New
 * thread" while everybody else read "Group thread" — for the same thread, at
 * the same moment. Every surface now treats all of them as "no name yet" and
 * renders `defaultThreadTitle` instead, so one thread reads one way.
 */
export function defaultThreadTitle(language?: 'en' | 'es' | null): string {
  return language === 'es' ? 'Nuevo hilo' : 'New thread';
}

export function isPlaceholderThreadTitle(raw: unknown): boolean {
  const value = typeof raw === 'string' ? raw.trim().toLowerCase() : '';
  return value.length === 0 || RESERVED_THREAD_TITLES.has(value);
}

/**
 * Accept only bounded human-facing titles. Known identifiers and Firebase-style
 * opaque ids are metadata, never display labels.
 */
export function normalizeCanonicalThreadTitle(
  raw: unknown,
  knownIds: readonly unknown[] = [],
): string | undefined {
  const value = typeof raw === 'string'
    ? raw.trim().slice(0, CANONICAL_THREAD_TITLE_MAX_LENGTH)
    : '';
  if (!value || RESERVED_THREAD_TITLES.has(value.toLowerCase())) {
    return undefined;
  }

  const ids = new Set(
    knownIds
      .map((id) => String(id ?? '').trim())
      .filter(Boolean),
  );
  return ids.has(value) ? undefined : value;
}
